import ReactSelect from 'components/base/ReactSelect';
import useDebounce from 'hooks/useDebounce';
import { useBranchContext } from 'providers/BranchProvider';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import { TSupplierDetails } from 'utils/types';
import * as yup from 'yup';

/** Shared boolean submit handle used by many CRM forms. */
export type PurchaseOrderHandle = {
  submit: () => Promise<boolean | undefined>;
};

/** Richer submit result for PO line-item add/edit (product match polling). */
export type PurchaseOrderItemSubmitResult =
  | boolean
  | {
      success: boolean;
      queuedProductMatch?: boolean;
      purchaseOrderItemId?: number;
    };

export type PurchaseOrderItemFormHandle = {
  submit: () => Promise<PurchaseOrderItemSubmitResult | undefined>;
};

export type SelectOption = {
  label: string;
  value: number;
};

const CreatePurchaseOrderForm = forwardRef<PurchaseOrderHandle>((_, ref) => {
  const { selectedBranch } = useBranchContext();
  const [suppliers, setSuppliers] = useState<TSupplierDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [supplierId, setSupplierId] = useState<number | null>(null);

  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 500);

  const fetchSupplier = async (search: string = '') => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/suppliers?page=1&pageSize=10&search=${search}`
      );
      setSuppliers(res.data.data);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const formattedSupplierOptions = suppliers?.map(supplier => ({
    value: supplier.supplierId,
    label: supplier.name
  }));

  const purchaseOrderSchema = yup.object().shape({
    supplierId: yup.number().required('Please select supplier')
  });

  const handleSubmit = async () => {
    try {
      await purchaseOrderSchema.validate({ supplierId }, { abortEarly: false });
      const res = await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders`,
        { supplierId }
      );
      const followUpItemCount = res.data?.followUpItemCount ?? 0;
      toast.success(
        followUpItemCount > 0
          ? `Purchase order created. ${followUpItemCount} unfulfilled item${
              followUpItemCount === 1 ? '' : 's'
            } from the previous order ${
              followUpItemCount === 1 ? 'was' : 'were'
            } added for follow up.`
          : 'Purchase order created.',
        {
          ...toastConfig,
          autoClose: 4000
        }
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  useEffect(() => {
    if (debouncedInput) {
      fetchSupplier(debouncedInput);
    } else {
      fetchSupplier();
    }
  }, [debouncedInput]);

  return (
    <Form className="w-100">
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Select Supplier</Form.Label>
          <ReactSelect
            placeholder="Select supplier"
            options={formattedSupplierOptions}
            onChange={newvalue =>
              setSupplierId((newvalue as SelectOption).value)
            }
            closeMenuOnSelect
            isLoading={isLoading}
            isSearchable
            onInputChange={newValue => setInput(newValue)}
          />
        </Form.Group>
      </Row>
    </Form>
  );
});

export default CreatePurchaseOrderForm;
